/**
 * Safe serialization utility for Sentry attributes.
 * Handles circular references, limits size, and redacts sensitive data.
 */

const MAX_RESPONSE_SIZE = 4096;
const MAX_DEPTH = 5;
const MAX_ARRAY_ITEMS = 10;

/**
 * Patterns that indicate sensitive data that should be redacted.
 */
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /api[_-]?key/i,
  /auth/i,
  /credential/i,
  /private/i,
  /bearer/i,
  /session/i,
  /cookie/i,
];

/**
 * Checks if a key name matches any sensitive pattern.
 */
function isSensitiveKey(key: string): boolean {
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(key));
}

/**
 * Recursively sanitizes an object for safe serialization.
 * - Redacts sensitive fields
 * - Handles circular references
 * - Limits depth and array length
 */
function sanitizeValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown {
  // Handle primitives
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value !== 'object') {
    // Truncate long strings
    if (typeof value === 'string' && value.length > 1000) {
      return `${value.slice(0, 1000)}...[truncated]`;
    }
    return value;
  }

  // Check depth limit
  if (depth >= MAX_DEPTH) {
    return '[max depth reached]';
  }

  // Check for circular reference
  if (seen.has(value as object)) {
    return '[circular]';
  }

  seen.add(value as object);

  // Handle arrays
  if (Array.isArray(value)) {
    const truncated = value.length > MAX_ARRAY_ITEMS;
    const items = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, depth + 1, seen));
    if (truncated) {
      items.push(`...[${value.length - MAX_ARRAY_ITEMS} more items]`);
    }
    return items;
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {};
  const entries = Object.entries(value as Record<string, unknown>);

  entries.forEach(([key, val]) => {
    if (isSensitiveKey(key)) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = sanitizeValue(val, depth + 1, seen);
    }
  });

  return sanitized;
}

/**
 * Serializes data for safe inclusion in Sentry attributes.
 *
 * Features:
 * - Maximum size limit (default 4KB)
 * - Sensitive field redaction (password, token, secret, etc.)
 * - Maximum depth of 5 levels
 * - Array truncation (first 10 items)
 * - Circular reference handling
 *
 * @param data - The data to serialize
 * @param maxSize - Maximum size of the resulting string (default 4096)
 * @returns Serialized string safe for Sentry
 */
export function serializeForSentry(
  data: unknown,
  maxSize: number = MAX_RESPONSE_SIZE,
): string {
  if (data === null || data === undefined) {
    return '';
  }

  try {
    const seen = new WeakSet<object>();
    const sanitized = sanitizeValue(data, 0, seen);
    let result = JSON.stringify(sanitized);

    // Truncate if exceeds max size
    if (result.length > maxSize) {
      result = `${result.slice(0, maxSize - 15)}...[truncated]`;
    }

    return result;
  } catch {
    return '[serialization failed]';
  }
}

export default serializeForSentry;
